import React, { useRef, useEffect, useState } from 'react';
import Button from '../../atoms/Button';
import './ScrollableCategories.scss';

interface Category {
  id: number | null;
  name: string;
}

interface ScrollableCategoriesProps {
  categories: Category[];
  selectedCategory: number | null;
  onCategorySelect: (categoryId: number | null) => void;
  className?: string;
}

const ScrollableCategories: React.FC<ScrollableCategoriesProps> = ({
  categories,
  selectedCategory,
  onCategorySelect,
  className = ''
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      return () => container.removeEventListener('scroll', checkScrollButtons);
    }
  }, [categories]);

    useEffect(() => {
    if (selectedCategory !== null) {
      // Pequeño delay para asegurar que el DOM se haya actualizado
      setTimeout(() => {
        scrollToSelectedCategory(selectedCategory);
      }, 100);
    }
  }, [selectedCategory, categories]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -200,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 200,
        behavior: 'smooth'
      });
    }
  };

  // Centrar segun seleccion de categoria
     const scrollToSelectedCategory = (categoryId: number | null) => {
    if (scrollContainerRef.current) {
      const selectedIndex = categories.findIndex(cat => cat.id === categoryId);
      if (selectedIndex !== -1) {
        const container = scrollContainerRef.current;
        const containerWidth = container.clientWidth;
        const content = container.querySelector('.scrollable-categories__content') as HTMLElement;
        
        if (content) {
          const buttons = content.querySelectorAll('.scrollable-categories__button');
          const selectedButton = buttons[selectedIndex] as HTMLElement;
          
          if (selectedButton) {
            const buttonLeft = selectedButton.offsetLeft;
            const buttonWidth = selectedButton.offsetWidth;
            const scrollLeft = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
            
            container.scrollTo({
              left: Math.max(0, scrollLeft),
              behavior: 'smooth'
            });
          }
        }
      }
    }
  };

  return (
    <div className={`scrollable-categories ${className}`}>
      {/* Left Arrow */}
      {showLeftArrow && (
        <button
          className="scrollable-categories__arrow scrollable-categories__arrow--left"
          onClick={scrollLeft}
          aria-label="Scroll left"
        >
          ‹
        </button>
      )}

      {/* Categories Container */}
      <div 
        className="scrollable-categories__container"
        ref={scrollContainerRef}
      >
        <div className="scrollable-categories__content">
          {categories.map((category) => (
            <Button
              key={category.id || 'all'}
              variant={selectedCategory === category.id ? 'primary' : 'outline'}
              size="smm"
              onClick = {() => {onCategorySelect(category.id); 
              scrollToSelectedCategory(category.id);}}   
              className="scrollable-categories__button"
            >
              {category.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Right Arrow */}
      {showRightArrow && (
        <button
          className="scrollable-categories__arrow scrollable-categories__arrow--right"
          onClick={scrollRight}
          aria-label="Scroll right"
        >
          ›
        </button>
      )}

      {/* Scroll Indicator */}
      <div className="scrollable-categories__indicator">
        <div className="scrollable-categories__indicator-dots">
          {Array.from({ length: Math.ceil(categories.length / 3) }).map((_, index) => (
            <div
              key={index}
              className={`scrollable-categories__dot ${
                index === 0 ? 'scrollable-categories__dot--active' : ''
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScrollableCategories;
